// Facebook Instagram Integration Factory
// Factory class for creating and managing Facebook/Instagram integration instances

import { FacebookInstagramIntegration } from './FacebookInstagramIntegration';
import { IntegrationConfig, EncryptedCredentials, IntegrationSettings } from '../../../types/integration';

export interface FacebookInstagramCredentials {
  accessToken: string;
  appId: string;
  appSecret: string;
  pageId?: string;
  instagramBusinessAccountId?: string;
}

export interface FacebookInstagramSettings extends IntegrationSettings {
  enableInstagram: boolean;
  enableFacebook: boolean;
  defaultPostingSchedule?: {
    facebook?: string; // cron expression
    instagram?: string; // cron expression
  };
  audienceSync: {
    enabled: boolean;
    syncFrequency: 'hourly' | 'daily' | 'weekly';
  };
  engagementTracking: {
    enabled: boolean;
    trackingFrequency: 'real_time' | 'hourly' | 'daily';
  };
}

export interface FacebookInstagramConfig extends IntegrationConfig {
  settings: FacebookInstagramSettings;
}

export class FacebookInstagramIntegrationFactory {
  private static instances: Map<string, FacebookInstagramIntegration> = new Map();

  /**
   * Create a new Facebook/Instagram integration instance
   */
  static async create(config: FacebookInstagramConfig): Promise<FacebookInstagramIntegration> {
    try {
      // Validate configuration
      this.validateConfig(config);

      // Create integration instance
      const integration = new FacebookInstagramIntegration(config);

      // Test connection
      const connectionTest = await integration.testConnection();
      if (!connectionTest.success) {
        throw new Error(`Connection test failed: ${connectionTest.message}`);
      }

      // Connect to platforms
      await integration.connect();

      // Store instance
      this.instances.set(config.id, integration);

      return integration;
    } catch (error) {
      throw new Error(`Failed to create Facebook/Instagram integration: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get existing integration instance
   */
  static getInstance(integrationId: string): FacebookInstagramIntegration | null {
    return this.instances.get(integrationId) || null;
  }

  /**
   * Remove integration instance
   */
  static async removeInstance(integrationId: string): Promise<void> {
    const integration = this.instances.get(integrationId);
    if (integration) {
      await integration.disconnect();
      this.instances.delete(integrationId);
    }
  }

  /**
   * List all active integration instances
   */
  static getActiveInstances(): FacebookInstagramIntegration[] {
    return Array.from(this.instances.values());
  }

  /**
   * Update integration configuration
   */
  static async updateConfig(integrationId: string, newConfig: Partial<FacebookInstagramConfig>): Promise<FacebookInstagramIntegration> {
    const existingIntegration = this.instances.get(integrationId);
    if (!existingIntegration) {
      throw new Error(`Integration ${integrationId} not found`);
    }

    // Disconnect existing integration
    await existingIntegration.disconnect();

    // Create new configuration
    const updatedConfig: FacebookInstagramConfig = {
      ...existingIntegration.getConfig(),
      ...newConfig
    } as FacebookInstagramConfig;

    // Validate new configuration
    this.validateConfig(updatedConfig);

    // Create new integration instance
    const newIntegration = new FacebookInstagramIntegration(updatedConfig);
    
    // Test and connect
    const connectionTest = await newIntegration.testConnection();
    if (!connectionTest.success) {
      throw new Error(`Connection test failed: ${connectionTest.message}`);
    }

    await newIntegration.connect();

    // Replace instance
    this.instances.set(integrationId, newIntegration);

    return newIntegration;
  }

  /**
   * Create integration from setup wizard data
   */
  static async createFromWizard(wizardData: {
    name: string;
    accessToken: string;
    appId: string;
    appSecret: string;
    selectedPages: string[];
    enableInstagram: boolean;
    enableFacebook: boolean;
    audienceSyncEnabled: boolean;
    engagementTrackingEnabled: boolean;
  }): Promise<FacebookInstagramIntegration> {
    // Create encrypted credentials (placeholder - in real implementation would encrypt)
    const encryptedCredentials: EncryptedCredentials = {
      encrypted: JSON.stringify({
        accessToken: wizardData.accessToken,
        appId: wizardData.appId,
        appSecret: wizardData.appSecret,
        pageId: wizardData.selectedPages[0]
      }),
      keyId: 'default-key',
      algorithm: 'AES-256-GCM',
      createdAt: new Date()
    };

    const config: FacebookInstagramConfig = {
      id: this.generateId(),
      name: wizardData.name,
      type: 'social',
      platform: 'facebook_instagram',
      status: 'active',
      credentials: encryptedCredentials,
      settings: {
        enabled: true,
        autoSync: true,
        batchSize: 100,
        rateLimitPerMinute: 60,
        retryAttempts: 3,
        timeoutMs: 30000,
        customSettings: {},
        enableInstagram: wizardData.enableInstagram,
        enableFacebook: wizardData.enableFacebook,
        audienceSync: {
          enabled: wizardData.audienceSyncEnabled,
          syncFrequency: 'daily'
        },
        engagementTracking: {
          enabled: wizardData.engagementTrackingEnabled,
          trackingFrequency: 'hourly'
        }
      },
      fieldMappings: [],
      syncSchedule: {
        enabled: true,
        frequency: 'real_time',
        timezone: 'UTC',
        batchSize: 100,
        conflictResolution: 'auto_source_wins'
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      healthStatus: {
        status: 'healthy',
        lastCheck: new Date(),
        responseTime: 0,
        errorRate: 0,
        uptime: 100,
        issues: []
      },
      metadata: {
        tags: ['social', 'facebook', 'instagram'],
        category: 'social_media',
        version: '1.0.0',
        supportedFeatures: ['post_publishing', 'audience_sync', 'engagement_tracking'],
        limitations: ['no_story_publishing', 'limited_instagram_insights'],
        customData: {}
      }
    };

    return await this.create(config);
  }

  /**
   * Test Facebook/Instagram credentials
   */
  static async testCredentials(credentials: {
    accessToken: string;
    appId: string;
    appSecret: string;
  }): Promise<{
    success: boolean;
    message: string;
    availablePages?: Array<{
      id: string;
      name: string;
      category: string;
      hasInstagram: boolean;
      instagramUsername?: string;
    }>;
  }> {
    try {
      // Create encrypted credentials for testing
      const encryptedCredentials: EncryptedCredentials = {
        encrypted: JSON.stringify(credentials),
        keyId: 'test-key',
        algorithm: 'AES-256-GCM',
        createdAt: new Date()
      };

      // Create temporary integration for testing
      const tempConfig: FacebookInstagramConfig = {
        id: 'temp-test',
        name: 'Test Integration',
        type: 'social',
        platform: 'facebook_instagram',
        status: 'configuring',
        credentials: encryptedCredentials,
        settings: {
          enabled: true,
          autoSync: false,
          batchSize: 100,
          rateLimitPerMinute: 60,
          retryAttempts: 3,
          timeoutMs: 30000,
          customSettings: {},
          enableInstagram: false,
          enableFacebook: true,
          audienceSync: { enabled: false, syncFrequency: 'daily' },
          engagementTracking: { enabled: false, trackingFrequency: 'hourly' }
        },
        fieldMappings: [],
        syncSchedule: {
          enabled: false,
          frequency: 'daily',
          timezone: 'UTC',
          batchSize: 100,
          conflictResolution: 'auto_source_wins'
        },
        createdAt: new Date(),
        updatedAt: new Date(),
        healthStatus: {
          status: 'healthy',
          lastCheck: new Date(),
          responseTime: 0,
          errorRate: 0,
          uptime: 100,
          issues: []
        },
        metadata: {
          tags: ['test'],
          category: 'social_media',
          version: '1.0.0',
          supportedFeatures: ['test'],
          limitations: [],
          customData: {}
        }
      };

      const tempIntegration = new FacebookInstagramIntegration(tempConfig);
      
      // Test connection
      const connectionResult = await tempIntegration.testConnection();
      if (!connectionResult.success) {
        return connectionResult;
      }

      // Get available pages
      await tempIntegration.connect();
      const platforms = await tempIntegration.getConnectedPlatforms();
      
      const availablePages = platforms
        .filter(platform => platform.name === 'facebook')
        .map(platform => ({
          id: platform.accountId,
          name: platform.accountName,
          category: 'page', // Would get from API
          hasInstagram: platforms.some(p => p.name === 'instagram' && p.accountName.includes(platform.accountName)),
          instagramUsername: platforms.find(p => p.name === 'instagram' && p.accountName.includes(platform.accountName))?.accountName
        }));

      return {
        success: true,
        message: 'Credentials valid',
        availablePages
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Credential test failed'
      };
    }
  }

  /**
   * Get integration health status
   */
  static async getHealthStatus(integrationId: string): Promise<{
    status: 'healthy' | 'warning' | 'error';
    issues: string[];
    lastCheck: Date;
    metrics: {
      postsPublished: number;
      engagementRate: number;
      audienceSize: number;
      lastSyncTime?: Date;
    };
  }> {
    const integration = this.instances.get(integrationId);
    if (!integration) {
      return {
        status: 'error',
        issues: ['Integration not found'],
        lastCheck: new Date(),
        metrics: {
          postsPublished: 0,
          engagementRate: 0,
          audienceSize: 0
        }
      };
    }

    try {
      // Test connection
      const connectionTest = await integration.testConnection();
      const issues: string[] = [];
      
      if (!connectionTest.success) {
        issues.push(`Connection failed: ${connectionTest.message}`);
      }

      // Check platform connections
      const platforms = await integration.getConnectedPlatforms();
      const disconnectedPlatforms = platforms.filter(p => !p.connected);
      
      if (disconnectedPlatforms.length > 0) {
        issues.push(`Disconnected platforms: ${disconnectedPlatforms.map(p => p.displayName).join(', ')}`);
      }

      // Get metrics (placeholder implementation)
      const metrics = {
        postsPublished: 0, // Would get from analytics
        engagementRate: 0, // Would calculate from recent posts
        audienceSize: 0, // Would get from audience APIs
        lastSyncTime: integration.getLastSyncTime()
      };

      const status = issues.length === 0 ? 'healthy' : 
                    issues.some(issue => issue.includes('Connection failed')) ? 'error' : 'warning';

      return {
        status,
        issues,
        lastCheck: new Date(),
        metrics
      };
    } catch (error) {
      return {
        status: 'error',
        issues: [`Health check failed: ${error instanceof Error ? error.message : 'Unknown error'}`],
        lastCheck: new Date(),
        metrics: {
          postsPublished: 0,
          engagementRate: 0,
          audienceSize: 0
        }
      };
    }
  }

  /**
   * Validate integration configuration
   */
  private static validateConfig(config: FacebookInstagramConfig): void {
    // Decrypt credentials for validation (in real implementation would properly decrypt)
    let credentials: FacebookInstagramCredentials;
    try {
      credentials = JSON.parse(config.credentials.encrypted) as FacebookInstagramCredentials;
    } catch (error) {
      throw new Error('Invalid encrypted credentials format');
    }

    if (!credentials.accessToken) {
      throw new Error('Access token is required');
    }

    if (!credentials.appId) {
      throw new Error('App ID is required');
    }

    if (!credentials.appSecret) {
      throw new Error('App secret is required');
    }

    if (!config.settings.enableFacebook && !config.settings.enableInstagram) {
      throw new Error('At least one platform (Facebook or Instagram) must be enabled');
    }

    if (config.settings.enableInstagram && !credentials.instagramBusinessAccountId) {
      console.warn('Instagram enabled but no Instagram Business Account ID provided');
    }
  }

  /**
   * Generate unique integration ID
   */
  private static generateId(): string {
    return `facebook_instagram_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}